import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { useAppSelector } from '../../data/store'
import PrequalificationApprovedForm from './PrequalificationApprovedForm'
import { Enum } from '../../data/types/constants'
import Loader from '../components/Loader'
import CreditApplicationContainerStepProgressIcon from './CreditApplicationContainerStepProgressIcon'
import { trackingEvents } from '../utils/trackingEvents'

function PrequalificationDecisionContainer() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatchEffect = useSideEffect()
  const creditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const stepProgression = <CreditApplicationContainerStepProgressIcon currentStep={1} />

  const onAcceptHardHitConditions = () => {
    dispatchEffect(creditApplicationEffects.postQualify(id as string)).then(() => {
      navigate(`/creditApplication/${id as string}`, { replace: true })
    })
  }

  const gotoAddCoapplicant = () => {
    navigate(`/creditApplication/${id as string}/coapplicant`)
  }

  const prequalificationAmount = Math.min(
    creditApplication?.requestedLoanAmount ?? Number.MAX_VALUE,
    creditApplication?.prequalificationDecision?.maxLoanAmount ?? Number.MAX_VALUE,
  )
  const cancelMyApp = (event: { preventDefault: () => void }) => {
    trackingEvents.withdrewAfterPrequal({
      credit_application_id: id as string,
      reference_number: creditApplication?.referenceNumber as number,
      amount: prequalificationAmount,
    })
    navigate(`/creditApplication/${id as string}/reportFeedback`, { replace: true })
    event.preventDefault()
  }

  React.useEffect(() => {
    if (
      creditApplication &&
      creditApplication?.prequalificationDecision?.decision === Enum.EPrequalificationDecision.Refused
    ) {
      trackingEvents.prequalDeclined({
        credit_application_id: creditApplication.id,
        reference_number: creditApplication.referenceNumber,
        amount: prequalificationAmount,
      })
    }
  }, [creditApplication])

  return (
    <div>
      {(creditApplication == null ||
        creditApplication?.prequalificationDecision === null ||
        creditApplication?.prequalificationDecision.decision === Enum.EPrequalificationDecision.Pending) && (
        <Loader
          title={t('prequalification.processing')}
          description={`${t('common.pleaseWait')} ${t('common.withYouShortly')}`}
        />
      )}

      {creditApplication?.prequalificationDecision !== null && (
        <>
          {creditApplication?.prequalificationDecision.decision === Enum.EPrequalificationDecision.Approved && (
            <PrequalificationApprovedForm
              onSubmit={onAcceptHardHitConditions}
              cancelMyApp={cancelMyApp}
              gotoAddCoapplicant={gotoAddCoapplicant}
              stepProgression={stepProgression}
            />
          )}

          {creditApplication?.prequalificationDecision.decision === Enum.EPrequalificationDecision.Refused && (
            <main className="general-message" style={{ paddingTop: '15rem' }}>
              {stepProgression}

              <h4 className="subtitle">{t<string>('common.financeApplication')}</h4>
              <form onSubmit={gotoAddCoapplicant}>
                <h1>{t<string>('common.refused', { forStep: t<string>('common.prequalified') })}</h1>
                <button type="submit" className="btn btn-blue" disabled={creditApplication?.hasCoapplicant}>
                  <span> {t('coapplicant.addCoapplicant')}</span>
                  <i className="fa-regular fa-arrow-right" />
                </button>
              </form>
            </main>
          )}

          {creditApplication?.prequalificationDecision.decision === Enum.EPrequalificationDecision.Error && (
            <main className="general-message" style={{ paddingTop: '15rem' }}>
              {stepProgression}

              <h4 className="subtitle">{t<string>('common.financeApplication')}</h4>
              <h1>{t<string>('prequalification.errorWhileValidatingIdentity')}</h1>
            </main>
          )}
        </>
      )}
    </div>
  )
}

export default PrequalificationDecisionContainer
